import { Component, DestroyRef, inject, signal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, Observable, of } from 'rxjs';

export type PopupType = 'delete' | 'error' | 'reset' | 'info' | 'confirm' | 'subscribe' | 'unsubscribe';
export type PopupValue =
  | string
  | { errorType: string; errorMessage: string; errorCode: string }
  | {
      code: number;
      error: string;
    };
export type PopupData = { type: PopupType; value?: PopupValue; cssClass?: string; message?: string; icon?: string };

@Component({
  imports: [TranslatePipe, MatIcon, MatButtonModule],
  selector: 'iot-platform-ui-popup',
  templateUrl: './popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./popup.component.scss']
})
export class PopupComponent {
  public data: PopupData = inject(MAT_DIALOG_DATA);
  public dialogRef: MatDialogRef<PopupComponent> = inject(MatDialogRef<PopupComponent>);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private static readonly WARNING_POPUP_TYPES: PopupType[] = ['error', 'delete', 'reset', 'confirm', 'unsubscribe'];
  private static readonly TRANSLATION_KEYS_BY_TYPE: Partial<Record<PopupType, string>> = {
    delete: 'POPUP.DELETE_TYPE',
    subscribe: 'POPUP.SUBSCRIBE_TYPE',
    unsubscribe: 'POPUP.UNSUBSCRIBE_TYPE',
    error: 'POPUP.ERROR_TYPE',
    reset: 'POPUP.RESET_TYPE',
    info: 'POPUP.INFO_TYPE',
    confirm: 'POPUP.CONFIRM_TYPE'
  };

  popupMessage: Signal<string> = signal(this.getMessage(this.data));

  popupInfo = toSignal(
    this.getPopupInfo(this.data.type).pipe(
      map((data) => (data.value ? data.value : data)),
      takeUntilDestroyed(this.destroyRef)
    )
  );

  get color(): { important: boolean } {
    return { important: PopupComponent.WARNING_POPUP_TYPES.includes(this.data.type) };
  }

  close(value: boolean): void {
    this.dialogRef.close(value);
  }

  private getMessage(data: PopupData): string {
    const value = data.value as any;
    if (value?.error?.error?.error) {
      return `${value.type} ${this.translateService.instant(value.error.error.error as string)}`;
    }
    if (data.type === 'error' && value && typeof value === 'object' && 'error' in value) {
      return 'Unable to reach the service';
    }
    return value ? value : 'An error has occurred, please try again later';
  }

  private getPopupInfo(type: PopupType): Observable<any> {
    const key = PopupComponent.TRANSLATION_KEYS_BY_TYPE[type];
    return key ? this.translateService.get(key) : of(this.data);
  }
}
